import {
  CustomFieldsEditor,
  CollapsibleSection,
  TextInputField,
  Flex
} from '@sequencehq/core-components'
import { CustomerSelect } from 'Customer/index'
import { CustomerAliasField } from '../components/CustomerAliasField'
import {
  CustomerCustomPropertiesConfig,
  CustomerCustomProperty,
  CustomerFormFieldControls
} from '../types'
import { useSectionExpansion } from '../useSectionExpansion'
import { fieldStyles, inputStyles } from './styles'

export type OtherInformationSectionProps = {
  labelField: CustomerFormFieldControls['label']
  aliasesField: CustomerFormFieldControls['aliases']
  /** Omit to hide the parent customer select. */
  parentIdField?: CustomerFormFieldControls['parentId']
  /** The hook's `customProperties` return. */
  customProperties: CustomerCustomPropertiesConfig
  lockedAliasCount?: number
  'data-testid'?: string
}

export const OtherInformationSection = ({
  labelField,
  aliasesField,
  parentIdField,
  customProperties,
  lockedAliasCount,
  'data-testid': testId
}: OtherInformationSectionProps) => {
  const { isOpen, onToggle } = useSectionExpansion(
    [labelField, aliasesField, parentIdField].some(
      field => field && field.validationErrors.length > 0
    )
  )

  return (
    <CollapsibleSection
      title="Other information"
      isOpen={isOpen}
      onToggle={onToggle}
      data-testid={testId}
    >
      <Flex direction="column" gap="12px">
        <TextInputField
          data-testid={testId ? `${testId}.label` : undefined}
          label="Label (optional)"
          value={labelField.value}
          onChange={e => labelField.onChange(e.target.value)}
          validationErrors={labelField.validationErrors}
          {...inputStyles}
          styles={fieldStyles}
          placeholder="Label"
        />
        {parentIdField && (
          <CustomerSelect
            data-testid={testId ? `${testId}.parentCustomer` : undefined}
            label="Parent customer (optional)"
            value={parentIdField.value}
            onChange={(customerId: string) => {
              parentIdField.onChange(customerId)
            }}
            styles={fieldStyles}
            placeholder="Select parent customer"
          />
        )}
        <CustomerAliasField
          data-testid={testId ? `${testId}.aliases` : undefined}
          aliases={aliasesField.value}
          onChange={newAliases => aliasesField.onChange(newAliases)}
          lockedAliasCount={lockedAliasCount}
        />
        <CustomFieldsEditor<CustomerCustomProperty>
          fields={customProperties.value}
          onAdd={customProperties.onAdd}
          onRemove={customProperties.onRemove}
          disabled={customProperties.disabled}
          title="Custom properties"
          addButtonLabel="Add custom property"
          addButtonVariant="ghost"
          data-testid={testId ? `${testId}.customProperties` : undefined}
          renderCells={(field, siblingFields, index) => (
            <CustomFieldsEditor.Row
              keyValue={field.key}
              siblingFields={siblingFields}
              onKeyChange={next =>
                customProperties.onUpdate(field.id, { key: next })
              }
              keyOptions={customProperties.keyOptions}
              value={field.value}
              onValueChange={next =>
                customProperties.onUpdate(field.id, { value: next })
              }
              disabled={customProperties.disabled}
              testIdPrefix={
                testId ? `${testId}.customProperties.row.${index}` : undefined
              }
            />
          )}
        />
      </Flex>
    </CollapsibleSection>
  )
}
